import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { createHash, randomBytes } from "node:crypto";

async function getSupabaseAdmin() {
  const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
  return supabaseAdmin;
}

async function assertAdmin(supabase: any, userId: string) {
  const { data, error } = await supabase
    .from("user_roles")
    .select("role")
    .eq("user_id", userId)
    .eq("role", "admin")
    .maybeSingle();
  if (error) throw new Error(error.message);
  if (!data) throw new Error("Forbidden: admin only");
}

function hashToken(token: string) {
  return createHash("sha256").update(token).digest("hex");
}

async function createAccessInvitation(email: string, createdBy: string) {
  const supabaseAdmin = await getSupabaseAdmin();
  const normalizedEmail = email.toLowerCase();
  const { data: existing } = await supabaseAdmin
    .from("invitations")
    .select("id")
    .eq("email", normalizedEmail)
    .is("used_at", null)
    .gt("expires_at", new Date().toISOString())
    .order("created_at", { ascending: false })
    .limit(25);

  const existingIds = (existing ?? []).map((row: any) => row.id);
  if (existingIds.length) {
    const { error: revokeError } = await supabaseAdmin
      .from("invitations")
      .delete()
      .in("id", existingIds);
    if (revokeError) throw new Error(revokeError.message);
  }

  const token = randomBytes(32).toString("base64url");
  const { error } = await supabaseAdmin.from("invitations").insert({
    email: normalizedEmail,
    token_hash: hashToken(token),
    role: "analyst",
    expires_at: new Date(Date.now() + 14 * 86400_000).toISOString(),
    created_by: createdBy,
  });
  if (error) throw new Error(error.message);
  return { token, alreadyExists: false };
}

async function listAllAuthUsers(supabaseAdmin: any) {
  const users: any[] = [];
  const perPage = 1000;
  for (let page = 1; page <= 20; page += 1) {
    const { data, error } = await supabaseAdmin.auth.admin.listUsers({ page, perPage });
    if (error) throw new Error(error.message);
    users.push(...(data?.users ?? []));
    if ((data?.users?.length ?? 0) < perPage) break;
  }
  return users;
}

function normalizeEmail(email: string | null | undefined) {
  return (email ?? "").trim().toLowerCase();
}

function requestStatusLabel(status: string | null | undefined, hasAuthUser: boolean) {
  if (hasAuthUser) return "registered";
  if (status === "approved") return "approved_awaiting_activation";
  if (status === "rejected") return "rejected_request";
  return "pending_request";
}

/** Submit an access request (public). */
export const submitAccessRequest = createServerFn({ method: "POST" })
  .inputValidator((input: unknown) =>
    z
      .object({
        full_name: z.string().trim().min(1).max(120),
        email: z.string().trim().email().max(255),
        organization: z.string().trim().max(160).optional().nullable(),
        reason: z.string().trim().min(10).max(2000),
      })
      .parse(input),
  )
  .handler(async ({ data }) => {
    const supabaseAdmin = await getSupabaseAdmin();
    const { error } = await supabaseAdmin.from("access_requests").insert({
      full_name: data.full_name,
      email: data.email.toLowerCase(),
      organization: data.organization ?? null,
      reason: data.reason,
    });
    if (error) throw new Error(error.message);
    return { ok: true };
  });

/** Admin creates a new analyst/admin user. */
export const adminCreateUser = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) =>
    z
      .object({
        email: z.string().trim().email().max(255),
        password: z.string().min(8).max(128),
        display_name: z.string().trim().max(120).optional(),
        role: z.enum(["admin", "analyst"]).default("analyst"),
      })
      .parse(input),
  )
  .handler(async ({ data, context }) => {
    await assertAdmin(context.supabase, context.userId);
    const supabaseAdmin = await getSupabaseAdmin();
    const { data: created, error } = await supabaseAdmin.auth.admin.createUser({
      email: data.email.toLowerCase(),
      password: data.password,
      email_confirm: true,
      user_metadata: { display_name: data.display_name ?? null },
    });
    if (error) throw new Error(error.message);
    if (created.user) {
      const userEmail = created.user.email ?? data.email.toLowerCase();
      const displayName = data.display_name || created.user.email?.split("@")[0] || null;
      await supabaseAdmin.from("profiles").upsert(
        {
          user_id: created.user.id,
          email: userEmail,
          display_name: displayName,
          clearance_level: data.role === "admin" ? "L3" : "L1",
        },
        { onConflict: "user_id" },
      );
      await supabaseAdmin.from("user_roles").upsert(
        { user_id: created.user.id, role: data.role },
        { onConflict: "user_id,role", ignoreDuplicates: true },
      );
    }
    return { ok: true, userId: created.user?.id };
  });

/** Admin lists access requests. */
export const listAccessRequests = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    await assertAdmin(context.supabase, context.userId);
    const supabaseAdmin = await getSupabaseAdmin();
    const { data, error } = await supabaseAdmin
      .from("access_requests")
      .select("*")
      .order("created_at", { ascending: false });
    if (error) throw new Error(error.message);
    return data ?? [];
  });

/** Admin updates the status of an access request. */
export const updateAccessRequest = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) =>
    z
      .object({
        id: z.string().uuid(),
        status: z.enum(["pending", "approved", "rejected"]),
      })
      .parse(input),
  )
  .handler(async ({ data, context }) => {
    await assertAdmin(context.supabase, context.userId);
    const supabaseAdmin = await getSupabaseAdmin();
    const { data: requestRow, error: requestError } = await supabaseAdmin
      .from("access_requests")
      .select("email, full_name")
      .eq("id", data.id)
      .maybeSingle();
    if (requestError) throw new Error(requestError.message);
    if (!requestRow) throw new Error("Access request not found");

    const { error } = await supabaseAdmin
      .from("access_requests")
      .update({
        status: data.status,
        reviewed_at: new Date().toISOString(),
        reviewed_by: context.userId,
      })
      .eq("id", data.id);
    if (error) throw new Error(error.message);

    if (data.status === "approved") {
      const invite = await createAccessInvitation(requestRow.email, context.userId);
      return {
        ok: true,
        invitationEmail: requestRow.email,
        invitationToken: invite.token,
        invitationAlreadyExists: invite.alreadyExists,
      };
    }

    return { ok: true };
  });

/** Admin lists all users with their roles. */
export const listUsers = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    await assertAdmin(context.supabase, context.userId);
    const supabaseAdmin = await getSupabaseAdmin();
    const authUsers = await listAllAuthUsers(supabaseAdmin);
    const [{ data: roles }, { data: profiles }, { data: requests }, { data: invitations }] = await Promise.all([
      supabaseAdmin.from("user_roles").select("*"),
      supabaseAdmin.from("profiles").select("user_id, email, display_name, clearance_level, created_at"),
      supabaseAdmin.from("access_requests").select("id, email, full_name, organization, status, reason, created_at, reviewed_at").order("created_at", { ascending: false }),
      supabaseAdmin.from("invitations").select("id, email, role, used_at, expires_at, created_at").order("created_at", { ascending: false }),
    ]);
    const profileByUser = new Map((profiles ?? []).map((p: any) => [p.user_id, p]));
    const profileByEmail = new Map<string, any>();
    for (const profile of profiles ?? []) {
      const email = normalizeEmail(profile.email);
      if (email && !profileByEmail.has(email)) profileByEmail.set(email, profile);
    }
    const rolesByUser = new Map<string, string[]>();
    for (const role of roles ?? []) {
      const bucket = rolesByUser.get(role.user_id) ?? [];
      bucket.push(role.role);
      rolesByUser.set(role.user_id, bucket);
    }
    const requestByEmail = new Map<string, any>();
    for (const request of requests ?? []) {
      const email = normalizeEmail(request.email);
      if (email && !requestByEmail.has(email)) requestByEmail.set(email, request);
    }
    const invitationByEmail = new Map<string, any>();
    for (const invitation of invitations ?? []) {
      const email = normalizeEmail(invitation.email);
      if (email && !invitationByEmail.has(email)) invitationByEmail.set(email, invitation);
    }
    const userByEmail = new Map<string, any>();
    for (const user of authUsers) {
      const email = normalizeEmail(user.email);
      if (email) userByEmail.set(email, user);
    }

    const rows = authUsers.map((u) => {
      const email = normalizeEmail(u.email);
      const profile = profileByUser.get(u.id) as any;
      const request = email ? requestByEmail.get(email) ?? null : null;
      return {
      id: u.id,
      person_key: `user:${u.id}`,
      auth_user_id: u.id,
      email: u.email,
      display_name: profile?.display_name ?? u.user_metadata?.display_name ?? null,
      clearance_level: profile?.clearance_level ?? null,
      access_request: request,
      invitation: email ? invitationByEmail.get(email) ?? null : null,
      account_status: requestStatusLabel(request?.status, true),
      can_grant_premium: true,
      created_at: profile?.created_at ?? u.created_at,
      last_sign_in_at: u.last_sign_in_at,
      roles: rolesByUser.get(u.id) ?? [],
      };
    });

    for (const request of requests ?? []) {
      const email = normalizeEmail(request.email);
      if (!email || userByEmail.has(email)) continue;
      const profile = profileByEmail.get(email) as any;
      const status = requestStatusLabel(request.status, false);
      rows.push({
        id: request.id,
        person_key: `request:${request.id}`,
        auth_user_id: null,
        email: request.email,
        display_name: profile?.display_name ?? request.full_name ?? null,
        clearance_level: profile?.clearance_level ?? null,
        access_request: request,
        invitation: invitationByEmail.get(email) ?? null,
        account_status: status,
        can_grant_premium: false,
        created_at: request.reviewed_at ?? request.created_at,
        last_sign_in_at: null,
        roles: [],
      });
    }

    return rows.sort((a, b) => new Date(b.created_at ?? 0).getTime() - new Date(a.created_at ?? 0).getTime());
  });

/** Admin deletes a user. */
export const adminDeleteUser = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) =>
    z.object({ userId: z.string().uuid() }).parse(input),
  )
  .handler(async ({ data, context }) => {
    await assertAdmin(context.supabase, context.userId);
    const supabaseAdmin = await getSupabaseAdmin();
    if (data.userId === context.userId) {
      throw new Error("Cannot delete yourself");
    }
    const { error } = await supabaseAdmin.auth.admin.deleteUser(data.userId);
    if (error) throw new Error(error.message);
    return { ok: true };
  });

/** Returns the current user's role (or null). */
export const getMyRole = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { data } = await context.supabase
      .from("user_roles")
      .select("role")
      .eq("user_id", context.userId);
    const roles = (data ?? []).map((r: any) => r.role as string);
    return {
      userId: context.userId,
      roles,
      isAdmin: roles.includes("admin"),
    };
  });